import React, { useState, useEffect, useMemo, memo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  Button,
  IconButton,
  Skeleton,
  Paper,
  Divider,
  alpha,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import WifiIcon from '@mui/icons-material/Wifi';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import PoolIcon from '@mui/icons-material/Pool';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import SpaIcon from '@mui/icons-material/Spa';
import TvIcon from '@mui/icons-material/Tv';
import FreeBreakfastIcon from '@mui/icons-material/FreeBreakfast';
import BathtubIcon from '@mui/icons-material/Bathtub';
import BedIcon from '@mui/icons-material/Bed';
import PersonIcon from '@mui/icons-material/Person';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import LandscapeIcon from '@mui/icons-material/Landscape';
import { getHotelInfo, getHotelAmenities, getHotelRoomTypes } from './nexastayApi';

import { useNexaStayTheme } from './NexaStayThemeContext';
import { useNexaStayBasePath, nexastayPath, resolveNexaStayHotelId } from './useNexaStayBasePath';

function formatTimeHHMM(str) {
  if (!str || typeof str !== 'string') return null;
  const parts = str.trim().split(':');
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1] || '0', 10);
  if (isNaN(h)) return null;
  const mm = String(m).padStart(2, '0');
  if (h === 0) return `12:${mm} AM`;
  if (h === 12) return `12:${mm} PM`;
  if (h > 12) return `${h - 12}:${mm} PM`;
  return `${h}:${mm} AM`;
}

const AMENITY_ICON_MAP = {
  wifi: WifiIcon,
  ac: AcUnitIcon,
  air_conditioning: AcUnitIcon,
  pool: PoolIcon,
  swimming_pool: PoolIcon,
  parking: LocalParkingIcon,
  restaurant: RestaurantIcon,
  dining: RestaurantIcon,
  gym: FitnessCenterIcon,
  fitness: FitnessCenterIcon,
  spa: SpaIcon,
  tv: TvIcon,
  television: TvIcon,
  breakfast: FreeBreakfastIcon,
  bathtub: BathtubIcon,
  bathroom: BathtubIcon,
};

function getAmenityIcon(a) {
  const codeKey = (a.code || '').toLowerCase().replace(/[\s-]+/g, '_');
  const nameKey = (a.name || '').toLowerCase().replace(/[\s-]+/g, '_');
  return (
    AMENITY_ICON_MAP[(a.icon || '').toLowerCase()] ||
    AMENITY_ICON_MAP[codeKey] ||
    AMENITY_ICON_MAP[nameKey] ||
    AMENITY_ICON_MAP[nameKey?.split('_')[0]] ||
    CheckroomIcon
  );
}

const PLACEHOLDER_IMG = 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&q=80';

const ImageSlider = memo(function ImageSlider({ images, alt, minHeight = 280, accent = '#4ce1b6', arrowVariant = 'dark' }) {
  const [active, setActive] = useState(0);
  const n = images.length;
  const next = () => setActive((a) => (a + 1) % n);
  const prev = () => setActive((a) => (a - 1 + n) % n);
  const isLight = arrowVariant === 'light';
  const arrowSx = isLight
    ? { bgcolor: 'rgba(255,255,255,0.92)', color: 'rgba(0,0,0,0.75)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }
    : { bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(76,225,182,0.3)' } };

  if (!n) return null;

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', borderRadius: 2 }}>
      <Box sx={{ position: 'relative', width: '100%', height: '100%', minHeight }}>
        {images.map((src, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: i === active ? 1 : 0,
              transition: 'opacity 0.5s ease',
              pointerEvents: i === active ? 'auto' : 'none',
            }}
          >
            <Box
              component="img"
              src={src}
              alt={`${alt} ${i + 1}`}
              loading={i === 0 ? 'eager' : 'lazy'}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>
        ))}
      </Box>
      {n > 1 && (
        <>
          <IconButton onClick={prev} sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', ...arrowSx }} size="small">
            <ChevronLeftIcon />
          </IconButton>
          <IconButton onClick={next} sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', ...arrowSx }} size="small">
            <ChevronRightIcon />
          </IconButton>
          <Stack direction="row" justifyContent="center" spacing={0.5} sx={{ position: 'absolute', bottom: 12, left: 0, right: 0 }}>
            {images.map((_, i) => (
              <Box
                key={i}
                onClick={() => setActive(i)}
                sx={{
                  width: i === active ? 20 : 8,
                  height: 8,
                  borderRadius: 1,
                  bgcolor: i === active ? accent : 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
});


export default function AboutPage() {
  const { hotelId: routeHotelId } = useParams();
  const hotelId = resolveNexaStayHotelId(routeHotelId);
  const basePath = useNexaStayBasePath();
  const [hotel, setHotel] = useState(null);
  const [amenities, setAmenities] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!hotelId) return;
    let cancelled = false;
    (async () => {
      try {
        const [hotelData, amenitiesData, roomTypesData] = await Promise.all([
          getHotelInfo(hotelId),
          getHotelAmenities(hotelId).catch(() => ({ amenities: [] })),
          getHotelRoomTypes(hotelId).catch(() => ({ room_types: [] })),
        ]);
        if (cancelled) return;
        setHotel(hotelData);
        setAmenities(amenitiesData?.amenities ?? []);
        setRoomTypes(roomTypesData?.room_types ?? []);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load hotel.');
      }
    })();
    return () => { cancelled = true; };
  }, [hotelId]);

  const theme = useNexaStayTheme();
  const accent = theme.accent;
  const textMuted = theme.textMuted ?? theme.textSecondary;
  const mainBgSx = { minHeight: '100vh', bgcolor: theme.bg };
  const propertyImages = useMemo(
    () => (hotel?.images?.length ? hotel.images : (hotel?.image_url ? [hotel.image_url] : [PLACEHOLDER_IMG])),
    [hotel?.images, hotel?.image_url]
  );
  const galleryImages = useMemo(() => {
    if (!propertyImages.length) return [PLACEHOLDER_IMG];
    return propertyImages;
  }, [propertyImages]);
  const aboutParagraphs = useMemo(() => {
    if (!hotel?.description || !String(hotel.description).trim()) return [];
    return hotel.description.split(/\n\n+/).filter(Boolean);
  }, [hotel?.description]);

  if (!hotelId) {
    return (
      <Box sx={{ ...mainBgSx, p: 3, color: theme.textPrimary }}>
        <Alert severity="warning" sx={{ bgcolor: theme.paper, color: theme.textPrimary, '& .MuiAlert-message': { color: theme.textPrimary }, '& .MuiAlert-icon': { color: '#ff9800' } }}>Missing hotel ID.</Alert>
      </Box>
    );
  }

  if (hotel === null && !error) {
    return (
      <Box sx={{ ...mainBgSx, minHeight: '50vh', pb: 3 }}>
        <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 1.5, sm: 2 }, py: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.1fr' }, gap: 2, mb: 2 }}>
            <Box sx={{ p: 3 }}>
              <Skeleton variant="text" width="60%" height={32} sx={{ bgcolor: 'rgba(255,255,255,0.08)', mb: 2 }} />
              <Skeleton variant="text" width="100%" sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
              <Skeleton variant="text" width="90%" sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
              <Skeleton variant="text" width="70%" sx={{ bgcolor: 'rgba(255,255,255,0.08)', mb: 2 }} />
              <Stack direction="row" spacing={2}>
                <Skeleton variant="rounded" width={100} height={48} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                <Skeleton variant="rounded" width={100} height={48} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
              </Stack>
            </Box>
            <Skeleton variant="rounded" height={280} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
          </Box>
          <Box sx={{ p: 3 }}>
            <Skeleton variant="text" width={200} height={28} sx={{ bgcolor: 'rgba(255,255,255,0.08)', mb: 2 }} />
            <Skeleton variant="rounded" height={260} sx={{ bgcolor: 'rgba(255,255,255,0.08)', mb: 2 }} />
            <Skeleton variant="rounded" height={260} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ ...mainBgSx, pb: 3, color: theme.textPrimary }}>
      {error && (
        <Box sx={{ maxWidth: 1400, mx: 'auto', px: 2, pb: 2 }}>
          <Alert severity="error" onClose={() => setError(null)} sx={{ bgcolor: theme.paper, color: theme.textPrimary, '& .MuiAlert-message': { color: theme.textPrimary }, '& .MuiAlert-icon': { color: '#f44336' } }}>{error}</Alert>
        </Box>
      )}

      <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 1.5, sm: 2 }, py: { xs: 3, sm: 4 }, bgcolor: theme.mode === 'dark' ? theme.bg : alpha(theme.accent, 0.02) }}>
        {/* Welcome section: Material UI Paper + creative layout */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 4 },
            mb: 4,
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: theme.paper,
            border: `1px solid ${theme.border}`,
            boxShadow: theme.mode === 'dark' ? '0 0 0 1px rgba(255,255,255,0.06)' : `0 8px 32px ${alpha(theme.accent, 0.06)}`,
          }}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.28fr 0.92fr' }, gap: { xs: 3, md: 4 }, alignItems: 'center' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: accent }} />
                <Typography
                  component="span"
                  sx={{
                    color: textMuted,
                    letterSpacing: '0.25em',
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    borderBottom: `2px solid ${accent}`,
                    pb: 0.5,
                    pt: 0.25,
                  }}
                >
                  Welcome to
                </Typography>
              </Box>
              <Typography
                component="h1"
                sx={{
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontWeight: 600,
                  color: theme.textPrimary,
                  mt: 1,
                  mb: 2,
                  fontSize: { xs: '1.85rem', sm: '2.25rem', md: '2.75rem' },
                  lineHeight: 0.96,
                }}
              >
                {hotel?.name || 'Hotel'}
              </Typography>
              {aboutParagraphs.map((p, i) => (
                <Typography key={i} variant="body1" sx={{ color: textMuted, maxWidth: '58ch', fontSize: '0.95rem', lineHeight: 1.7, mb: 1.5 }}>
                  {p}
                </Typography>
              ))}
            </Box>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                border: `1px solid ${theme.border}`,
                boxShadow: theme.mode === 'dark' ? '0 12px 40px rgba(0,0,0,0.25)' : `0 12px 40px ${alpha(theme.accent, 0.08)}`,
              }}
            >
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, p: 1, bgcolor: theme.paper }}>
                {galleryImages.map((src, i) => (
                  <Box
                    key={i}
                    component="img"
                    src={src}
                    alt={`${hotel?.name || 'Hotel'} ${i + 1}`}
                    loading={i < 3 ? 'eager' : 'lazy'}
                    sx={{
                      width: '100%',
                      aspectRatio: '1',
                      objectFit: 'cover',
                      borderRadius: 1.5,
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.03)',
                        boxShadow: theme.mode === 'dark' ? 4 : `0 8px 24px ${alpha(accent, 0.2)}`,
                      },
                    }}
                  />
                ))}
              </Box>
            </Paper>
          </Box>
        </Paper>

        {/* Rooms & Suites – Material UI Cards with elevation and hover */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Divider sx={{ flex: 1, borderColor: theme.borderAccent }} />
            <Typography
              component="h2"
              sx={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontWeight: 600,
                color: theme.textPrimary,
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.25rem' },
                lineHeight: 0.96,
              }}
            >
              Rooms & Suites
            </Typography>
            <Divider sx={{ flex: 1, borderColor: theme.borderAccent }} />
          </Box>

          {roomTypes.length === 0 ? (
            <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 2, border: `1px dashed ${theme.border}` }}>
              <Typography variant="body2" sx={{ color: textMuted }}>Room types will be listed here.</Typography>
            </Paper>
          ) : (
            <Stack spacing={3}>
              {roomTypes.map((rt, idx) => {
                const rtImages = rt.images?.length ? rt.images : (rt.image_url ? [rt.image_url] : [hotel?.image_url, PLACEHOLDER_IMG].filter(Boolean));
                const images = rtImages.length ? rtImages : [PLACEHOLDER_IMG];
                const isEven = idx % 2 === 0;
                const guests = rt.max_guests ?? rt.capacity ?? 2;
                const bedLabel = rt.base_adults === 1 ? '180 x 200' : '160 x 200';
                const areaLabel = rt.area_sqm ? `${rt.area_sqm} m²` : (idx === 0 ? '50 – 65 m²' : idx === 1 ? '30 – 40 m²' : '24 – 30 m²');
                const viewLabel = idx === 1 ? 'Garden View' : 'City View';
                const priceFrom = rt.rate_plan_rates?.[0]?.price ?? rt.min_price ?? null;
                const priceStr = priceFrom != null ? `₹ ${Number(priceFrom).toLocaleString('en-IN')}` : '—';

                const amenityRow = [
                  { icon: PersonIcon, label: String(guests) },
                  { icon: BedIcon, label: bedLabel },
                  { icon: SquareFootIcon, label: areaLabel },
                  { icon: LandscapeIcon, label: viewLabel },
                ];

                return (
                  <Paper
                    key={rt.id}
                    elevation={0}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      minHeight: { xs: 280, md: 340 },
                      borderRadius: 3,
                      overflow: 'hidden',
                      border: `1px solid ${theme.border}`,
                      bgcolor: theme.paper,
                      transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                      '&:hover': {
                        boxShadow: theme.mode === 'dark' ? '0 12px 40px rgba(0,0,0,0.4)' : `0 16px 48px ${alpha(accent, 0.12)}`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        order: { xs: 0, md: isEven ? 1 : 2 },
                        position: 'relative',
                        overflow: 'hidden',
                        width: '100%',
                        height: { xs: 280, md: 340 },
                        borderRadius: { xs: '12px 12px 0 0', md: isEven ? '0 12px 12px 0' : '12px 0 0 12px' },
                      }}
                    >
                      {images.length > 1 ? (
                        <ImageSlider images={images} alt={rt.name} minHeight={340} accent={accent} arrowVariant="light" />
                      ) : (
                        <Box component="img" src={images[0]} alt={rt.name} loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      )}
                    </Box>
                    <Box
                      sx={{
                        p: { xs: 2.5, sm: 3.5 },
                        order: { xs: 1, md: isEven ? 2 : 1 },
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        component="h3"
                        sx={{
                          fontFamily: 'Georgia, "Times New Roman", serif',
                          fontWeight: 600,
                          color: theme.textPrimary,
                          fontSize: { xs: '1.35rem', sm: '1.5rem' },
                          mb: 1.5,
                        }}
                      >
                        {rt.name}
                      </Typography>
                      {(rt.description && String(rt.description).trim()) ? (
                        <Typography variant="body1" sx={{ color: textMuted, lineHeight: 1.65, mb: 2, fontSize: '0.95rem' }}>
                          {rt.description}
                        </Typography>
                      ) : null}
                      <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
                        {amenityRow.map(({ icon: Icon, label }, i) => (
                          <Stack key={i} direction="row" alignItems="center" spacing={0.75}>
                            <Icon sx={{ fontSize: 20, color: accent }} />
                            <Typography variant="body2" sx={{ color: textMuted, fontSize: '0.9rem' }}>
                              {label}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" sx={{ mt: 'auto', pt: 1 }}>
                        <Button
                          component={Link}
                          to={nexastayPath(basePath, hotelId)}
                          variant="contained"
                          sx={{
                            bgcolor: accent,
                            color: theme.mode === 'dark' ? '#121212' : '#fff',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            px: 2.5,
                            py: 1.25,
                            borderRadius: 2,
                            boxShadow: `0 4px 14px ${alpha(accent, 0.4)}`,
                            '&:hover': { bgcolor: accent, opacity: 0.92, boxShadow: `0 6px 20px ${alpha(accent, 0.5)}` },
                          }}
                        >
                          Book
                        </Button>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                          <Typography component="span" sx={{ fontWeight: 700, color: theme.textPrimary, fontSize: '1.1rem' }}>
                            {priceStr}
                          </Typography>
                          <Typography component="span" variant="body2" sx={{ color: textMuted, fontSize: '0.85rem' }}>
                            per night
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  );
}
